import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API } from './constants';

export function CreateDeviationModal({ onClose, onCreated, RUH_CATEGORIES, t }) {
  const [type, setType] = useState('deviation');
  const [ruhCategory, setRuhCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [responsible, setResponsible] = useState('');
  const [deadline, setDeadline] = useState('');
  const [location, setLocation] = useState('');
  const [incidentDatetime, setIncidentDatetime] = useState('');
  const [immediateActions, setImmediateActions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      toast.error(t('validation.required'));
      return;
    }
    if (type === 'ruh' && !ruhCategory) {
      toast.error(t('cases.fields.selectCategory'));
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const payload = {
        type,
        title: title || null,
        description,
        severity,
        responsible: responsible || null,
        deadline: deadline ? new Date(deadline).toISOString() : null
      };

      // Add RUH-specific fields
      if (type === 'ruh') {
        payload.ruh_category = ruhCategory;
        payload.location = location || null;
        payload.incident_datetime = incidentDatetime ? new Date(incidentDatetime).toISOString() : null;
        payload.immediate_actions = immediateActions || null;
      }

      await axios.post(`${API}/ops/deviations`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('cases.caseRegistered'));
      onCreated();
    } catch (error) {
      toast.error(t('cases.couldNotRegister'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-deviation-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white mb-4">{t('cases.registerCase')}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('cases.fields.category')} *</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'ruh', label: t('cases.type.ruh'), desc: t('cases.type.ruhDesc'), color: 'orange' },
                { value: 'deviation', label: t('cases.type.deviation'), desc: t('cases.type.deviationDesc'), color: 'rose' },
                { value: 'improvement', label: t('cases.type.improvement'), desc: t('cases.type.improvementDesc'), color: 'blue' }
              ].map(typeOpt => (
                <button
                  key={typeOpt.value}
                  type="button"
                  onClick={() => setType(typeOpt.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    type === typeOpt.value
                      ? `border-${typeOpt.color}-500 bg-${typeOpt.color}-500/10`
                      : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                  }`}
                  data-testid={`type-btn-${typeOpt.value}`}
                >
                  <div className="font-medium text-slate-200">{typeOpt.label}</div>
                  <div className="text-xs text-slate-500">{typeOpt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* RUH Category */}
          {type === 'ruh' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Kategori *</label>
              <select
                value={ruhCategory}
                onChange={(e) => setRuhCategory(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                data-testid="ruh-category-select"
              >
                <option value="">Velg kategori...</option>
                {Object.entries(RUH_CATEGORIES || {}).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Tittel (valgfritt)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              placeholder="Kort tittel..."
              data-testid="deviation-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Beskrivelse *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              rows={3}
              placeholder={type === 'ruh' ? 'Beskriv hva som skjedde...' : 'Beskriv saken...'}
              autoFocus
              data-testid="deviation-description-input"
            />
          </div>

          {/* RUH-specific fields */}
          {type === 'ruh' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Når skjedde det?</label>
                  <input
                    type="datetime-local"
                    value={incidentDatetime}
                    onChange={(e) => setIncidentDatetime(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    data-testid="incident-datetime-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Hvor skjedde det?</label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    placeholder="Sted/lokasjon"
                    data-testid="location-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Umiddelbare tiltak</label>
                <textarea
                  value={immediateActions}
                  onChange={(e) => setImmediateActions(e.target.value)}
                  className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  rows={2}
                  placeholder="Hva ble gjort umiddelbart?"
                  data-testid="immediate-actions-input"
                />
              </div>
            </>
          )}

          {/* Severity */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Alvorlighet</label>
            <div className="flex gap-2">
              {[
                { value: 'low', label: 'Lav', activeClass: 'bg-emerald-500/20 border-emerald-500 text-emerald-400' },
                { value: 'medium', label: 'Middels', activeClass: 'bg-amber-500/20 border-amber-500 text-amber-400' },
                { value: 'high', label: 'Høy', activeClass: 'bg-rose-500/20 border-rose-500 text-rose-400' },
              ].map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSeverity(s.value)}
                  className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    severity === s.value ? s.activeClass : 'bg-slate-800/50 border-slate-700 text-slate-400'
                  }`}
                  data-testid={`severity-btn-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Responsible and Deadline */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Ansvarlig</label>
              <input
                type="text"
                value={responsible}
                onChange={(e) => setResponsible(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                placeholder="Navn eller rolle"
                data-testid="deviation-responsible-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Frist</label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                data-testid="deviation-deadline-input"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 font-medium transition-colors"
              data-testid="cancel-create-deviation"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-50 transition-colors shadow-lg shadow-cyan-500/25"
              data-testid="submit-create-deviation"
            >
              {submitting ? t('common.saving') : t('cases.registerCase')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
